import React, { useEffect, useMemo, useState } from 'react';
import { Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { transfers } from '../data/transfers';
import { clubs, getClubById } from '../data/clubs';

// Component to track zoom level
const ZoomTracker = ({ onZoomChange }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };
    
    map.on('zoomend', handleZoom);
    handleZoom(); // Get initial zoom
    
    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map, onZoomChange]);
  
  return null;
};

const TransferMap = ({ 
  selectedSellerLeague, 
  selectedBuyerLeague, 
  minAmount, 
  maxAmount, 
  groupingMode,
  selectedPosition,
  selectedTransferType,
  startDate,
  endDate,
  playerSearch,
  clubSearch,
  excludeOtherLeagues
}) => {
  const [zoomLevel, setZoomLevel] = useState(5); // Default zoom level
  const [currentBatch, setCurrentBatch] = useState(1); // Start with batch 1 (first 100)
  const [showMoreRoutes, setShowMoreRoutes] = useState({});
  const [clusteredClubs, setClusteredClubs] = useState({});
  const [activeClubTab, setActiveClubTab] = useState({}); // Track active tab for each club
  const filteredTransfers = useMemo(() => {
    console.log('Total transfers:', transfers.length);
    return transfers.filter(transfer => {
      
      const fromClub = getClubById(transfer.fromClubId);
      const toClub = getClubById(transfer.toClubId);
      
      // League filters
      if (selectedSellerLeague !== 'All' && fromClub?.league !== selectedSellerLeague) return false;
      if (selectedBuyerLeague !== 'All' && toClub?.league !== selectedBuyerLeague) return false;
      
      // Amount filter
      if (transfer.amount < minAmount || transfer.amount > maxAmount) return false;
      
      // Position filter
      if (selectedPosition !== 'All' && transfer.position !== selectedPosition) return false;
      
      // Transfer type filter
      if (selectedTransferType !== 'All') {
        if (transfer.type !== selectedTransferType) return false;
      }
      
      // Date range filter
      const transferDate = new Date(transfer.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (transferDate < start || transferDate > end) return false;
      
      // Player search filter
      if (playerSearch && !transfer.player.toLowerCase().includes(playerSearch.toLowerCase())) return false;
      
      // Club search filter
      if (clubSearch) {
        const fromClubName = fromClub?.name.toLowerCase() || '';
        const toClubName = toClub?.name.toLowerCase() || '';
        const searchTerm = clubSearch.toLowerCase();
        if (!fromClubName.includes(searchTerm) && !toClubName.includes(searchTerm)) return false;
      }
       
       // Exclude other leagues filter
       if (excludeOtherLeagues) {
         const majorLeagues = ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'];
         if (!majorLeagues.includes(fromClub?.league) && !majorLeagues.includes(toClub?.league)) {
           return false;
         }
       }
      
      return true;
    });
    
    // Performance-based filtering: Paginate through transfers by value
    const sortedTransfers = filteredTransfers.sort((a, b) => b.amount - a.amount);
    const batchSize = 100;
    const startIndex = 0;
    const endIndex = currentBatch * batchSize;
    const performanceFilteredTransfers = sortedTransfers.slice(startIndex, endIndex);
    
    console.log('Filtered transfers:', filteredTransfers.length, 'Showing batch', currentBatch, 'with', performanceFilteredTransfers.length, 'transfers');
    
    return performanceFilteredTransfers;
  }, [
    selectedSellerLeague, 
    selectedBuyerLeague, 
    minAmount, 
    maxAmount, 
    selectedPosition,
    selectedTransferType,
    startDate,
    endDate,
    playerSearch,
    clubSearch,
    excludeOtherLeagues,
    currentBatch
  ]);

  // Get total filtered transfers count for pagination
  const totalFilteredTransfers = useMemo(() => {
    return transfers.filter(transfer => {
      const fromClub = getClubById(transfer.fromClubId);
      const toClub = getClubById(transfer.toClubId);
      
      if (selectedSellerLeague !== 'All' && fromClub?.league !== selectedSellerLeague) return false;
      if (selectedBuyerLeague !== 'All' && toClub?.league !== selectedBuyerLeague) return false;
      if (transfer.amount < minAmount || transfer.amount > maxAmount) return false;
      if (selectedPosition !== 'All' && transfer.position !== selectedPosition) return false;
      if (selectedTransferType !== 'All' && transfer.type !== selectedTransferType) return false;
      
      const transferDate = new Date(transfer.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (transferDate < start || transferDate > end) return false;
      
      if (playerSearch && !transfer.player.toLowerCase().includes(playerSearch.toLowerCase())) return false;
      
      if (clubSearch) {
        const fromClubName = fromClub?.name.toLowerCase() || '';
        const toClubName = toClub?.name.toLowerCase() || '';
        const searchTerm = clubSearch.toLowerCase();
        if (!fromClubName.includes(searchTerm) && !toClubName.includes(searchTerm)) return false;
      }
       
      if (excludeOtherLeagues) {
        const majorLeagues = ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'];
        if (!majorLeagues.includes(fromClub?.league) && !majorLeagues.includes(toClub?.league)) {
          return false;
        }
      }
      
      return true;
    }).length;
  }, [
    selectedSellerLeague, 
    selectedBuyerLeague, 
    minAmount, 
    maxAmount, 
    selectedPosition,
    selectedTransferType,
    startDate,
    endDate,
    playerSearch,
    clubSearch,
    excludeOtherLeagues
  ]);

  // No clustering - show all clubs individually
  const clusteredClubsData = useMemo(() => {
    return clubs.map(club => ({ ...club, isCluster: false, clusterSize: 1 }));
  }, []);

  // Enhanced grouping logic with dots for all grouping modes
  const groupedTransfers = useMemo(() => {
    const groups = {};
    const facetGroups = {};
    
    filteredTransfers.forEach(transfer => {
      const fromClub = getClubById(transfer.fromClubId);
      const toClub = getClubById(transfer.toClubId);
      
      if (!fromClub || !toClub) return;
      
      if (groupingMode !== 'none') {
        // For all grouping modes, create league-to-league groups with facet breakdowns
        // Use a normalized key to treat bidirectional transfers as the same group
        const leagues = [fromClub.league, toClub.league].sort();
        const leagueKey = `${leagues[0]}-${leagues[1]}`;
        
        if (!facetGroups[leagueKey]) {
          facetGroups[leagueKey] = {
            fromLeague: leagues[0],
            toLeague: leagues[1],
            facets: {},
            totalValue: 0,
            totalTransfers: 0,
            bidirectional: fromClub.league !== toClub.league // Mark if it's bidirectional
          };
        }
        
        let facetValue;
        if (groupingMode === 'position') {
          facetValue = transfer.position;
        } else if (groupingMode === 'date') {
          facetValue = new Date(transfer.date).toISOString().slice(0, 7); // YYYY-MM
        } else if (groupingMode === 'league') {
          // For league grouping, create price range facets
          if (transfer.amount >= 100000000) {
            facetValue = '€100M+';
          } else if (transfer.amount >= 50000000) {
            facetValue = '€50M+';
          } else if (transfer.amount >= 20000000) {
            facetValue = '€20M+';
          } else if (transfer.amount > 0) {
            // Show actual range for paid transfers
            if (transfer.amount >= 10000000) {
              facetValue = '€10M-20M';
            } else if (transfer.amount >= 5000000) {
              facetValue = '€5M-10M';
            } else if (transfer.amount >= 1000000) {
              facetValue = '€1M-5M';
            } else {
              facetValue = '<€1M';
            }
      } else {
            facetValue = 'Free/Loan';
          }
        }
        
        if (facetValue) {
          if (!facetGroups[leagueKey].facets[facetValue]) {
            facetGroups[leagueKey].facets[facetValue] = {
              transfers: [],
              value: 0
            };
          }
          
          facetGroups[leagueKey].facets[facetValue].transfers.push(transfer);
          facetGroups[leagueKey].facets[facetValue].value += transfer.amount;
        }
        
        facetGroups[leagueKey].totalValue += transfer.amount;
        facetGroups[leagueKey].totalTransfers += 1;
      } else {
        // Standard club-to-club grouping (no grouping)
        const key = `club-${transfer.fromClubId}-${transfer.toClubId}`;
      
      if (!groups[key]) {
        groups[key] = {
          transfers: [],
          netValue: 0,
            fromEntity: fromClub.name,
            toEntity: toClub.name,
          fromClub: fromClub,
          toClub: toClub,
            groupType: 'club'
        };
      }
      groups[key].transfers.push(transfer);
      groups[key].netValue += transfer.amount;
      }
    });
    
    // Convert facet groups to standard format for rendering
    if (groupingMode !== 'none') {
      Object.entries(facetGroups).forEach(([key, group]) => {
        groups[key] = {
          transfers: Object.values(group.facets).flatMap(f => f.transfers),
          netValue: group.totalValue,
          fromEntity: group.fromLeague,
          toEntity: group.toLeague,
          fromClub: { league: group.fromLeague, lat: 0, lng: 0 },
          toClub: { league: group.toLeague, lat: 0, lng: 0 },
          groupType: groupingMode,
          facetBreakdown: group.facets,
          bidirectional: group.bidirectional
        };
      });
    }
    
    return groups;
  }, [filteredTransfers, groupingMode]);

  // Enhanced centers for different grouping types
  const getGroupCenters = useMemo(() => {
    if (groupingMode === 'league') {
      return {
        'Premier League': { lat: 51.5074, lng: -0.1278, country: 'England' },
        'La Liga': { lat: 40.4168, lng: -3.7038, country: 'Spain' },
        'Bundesliga': { lat: 52.5200, lng: 13.4050, country: 'Germany' },
        'Serie A': { lat: 41.9028, lng: 12.4964, country: 'Italy' },
        'Ligue 1': { lat: 48.8566, lng: 2.3522, country: 'France' }
      };
    } else if (groupingMode === 'position') {
      // For position grouping, we'll use league centers but show position info
      return {
        'Premier League': { lat: 51.5074, lng: -0.1278, country: 'England' },
        'La Liga': { lat: 40.4168, lng: -3.7038, country: 'Spain' },
        'Bundesliga': { lat: 52.5200, lng: 13.4050, country: 'Germany' },
        'Serie A': { lat: 41.9028, lng: 12.4964, country: 'Italy' },
        'Ligue 1': { lat: 48.8566, lng: 2.3522, country: 'France' }
      };
    } else if (groupingMode === 'date') {
      // For date grouping, we'll use league centers but show date info
      return {
        'Premier League': { lat: 51.5074, lng: -0.1278, country: 'England' },
        'La Liga': { lat: 40.4168, lng: -3.7038, country: 'Spain' },
        'Bundesliga': { lat: 52.5200, lng: 13.4050, country: 'Germany' },
        'Serie A': { lat: 41.9028, lng: 12.4964, country: 'Italy' },
        'Ligue 1': { lat: 48.8566, lng: 2.3522, country: 'France' }
      };
    }
    return null;
  }, [groupingMode]);

  const getLineColor = (amount) => {
    if (amount >= 100000000) return '#FF0000'; // Bright Red for €100M+
    if (amount >= 50000000) return '#FF6600';  // Bright Orange for €50M+
    if (amount >= 20000000) return '#FFCC00';  // Bright Yellow for €20M+
    return '#00CC00'; // Bright Green for smaller transfers
  };

  const getFacetColor = (facetValue, groupingMode) => {
    if (groupingMode === 'position') {
      const colors = {
        'Goalkeeper': '#FF0000',    // Bright Red
        'Defender': '#00CCFF',      // Bright Cyan
        'Midfielder': '#0066FF',    // Bright Blue
        'Forward': '#00FF00',       // Bright Green
        'Attacking Midfielder': '#0066FF',
        'Defensive Midfielder': '#00CCFF',
        'Centre-back': '#00CCFF',
        'Left-back': '#00CCFF',
        'Right-back': '#00CCFF',
        'Central Midfielder': '#0066FF',
        'Left Wing': '#00FF00',
        'Right Wing': '#00FF00',
        'Striker': '#00FF00',
        'Second Striker': '#00FF00',
        'Centre-Forward': '#00FF00',
        'Winger/Forward': '#00FF00'
      };
      return colors[facetValue] || '#FF00FF'; // Bright Magenta as fallback
    } else if (groupingMode === 'date') {
      // Generate bright colors based on month
      const monthColors = [
        '#FF0000', '#FF6600', '#FFCC00', '#00FF00', '#00CCFF', '#0066FF',
        '#CC00FF', '#FF0066', '#FF6600', '#00FF66', '#0066FF', '#FF00CC'
      ];
      const month = parseInt(facetValue.split('-')[1]) - 1; // Get month (0-11)
      return monthColors[month] || '#FF00FF'; // Bright Magenta as fallback
    } else if (groupingMode === 'league') {
      // Price range colors
      const colors = {
        '€100M+': '#FF0000',        // Bright Red for highest value
        '€50M+': '#FF6600',         // Bright Orange
        '€20M+': '#FFCC00',         // Bright Yellow
        '€10M-20M': '#FF0000',      // Bright Red for €10M-20M
        '€5M-10M': '#FF6600',       // Bright Orange for €5M-10M
        '€1M-5M': '#FFCC00',        // Bright Yellow for €1M-5M
        '<€1M': '#00CC00',          // Bright Green for smaller transfers
        'Free/Loan': '#00CCFF'      // Bright Cyan
      };
      return colors[facetValue] || '#FF00FF'; // Bright Magenta as fallback
    }
    return '#FF00FF'; // Bright Magenta as fallback
  };

  // Calculate line width based on number of transfers
  const getLineWidth = (transferCount) => {
    if (transferCount >= 50) return 4;
    if (transferCount >= 20) return 3;
    if (transferCount >= 10) return 2.5;
    if (transferCount >= 5) return 2;
    return 1.5;
  };

  const getClubIcon = (league, country = null) => {
    // Define base colors for top 5 leagues
    const baseColors = {
      'Premier League': '#3D195B',
      'La Liga': '#FF6B35',
      'Bundesliga': '#D62828',
      'Serie A': '#003F87',
      'Ligue 1': '#091C2E'
    };
    
    // Define top 5 countries and major leagues
    const top5Countries = ['England', 'Spain', 'Germany', 'Italy', 'France'];
    const majorLeagues = ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'];
    
    // Get base color for the league
    let color = baseColors[league] || '#666';
    
    // Determine if this is a major league club
    const isMajorLeague = majorLeagues.includes(league);
    const isTop5Country = country && top5Countries.includes(country);
    
    // If this is a club from a top 5 country, use a lighter shade
    if (isTop5Country) {
      // Convert hex to RGB, lighten it, and convert back to hex
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Lighten by 40% (mix with white)
      const lightenFactor = 0.4;
      const newR = Math.round(r + (255 - r) * lightenFactor);
      const newG = Math.round(g + (255 - g) * lightenFactor);
      const newB = Math.round(b + (255 - b) * lightenFactor);
      
      color = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    
    // Determine marker size and shape
    const size = isMajorLeague ? 20 : 14; // Smaller for non-major leagues
    const radius = isMajorLeague ? 8 : 6;
    const strokeWidth = isMajorLeague ? 2 : 1.5;
    
    // Use circles for major leagues, squares for others
    const shape = isMajorLeague ? 'circle' : 'rect';
    
    let svgContent;
    if (shape === 'circle') {
      svgContent = `<circle cx="${size/2}" cy="${size/2}" r="${radius}" fill="${color}" stroke="white" stroke-width="${strokeWidth}"/>`;
    } else {
      const rectSize = radius * 1.4; // Make square slightly larger than circle radius
      const rectX = (size - rectSize) / 2;
      const rectY = (size - rectSize) / 2;
      svgContent = `<rect x="${rectX}" y="${rectY}" width="${rectSize}" height="${rectSize}" fill="${color}" stroke="white" stroke-width="${strokeWidth}" rx="2"/>`;
    }
    
    return L.divIcon({
      className: 'custom-club-marker',
      html: `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        ${svgContent}
      </svg>`,
      iconSize: [size, size],
      iconAnchor: [size/2, size/2]
    });
  };

  // Calculate offset for overlapping lines
  const calculateOffset = (fromPos, toPos, index, total) => {
    if (total <= 1) return { lat: 0, lng: 0 };
    
    const dx = toPos[1] - fromPos[1];
    const dy = toPos[0] - fromPos[0];
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance === 0) return { lat: 0, lng: 0 };
    
    // Perpendicular vector
    const perpX = -dy / distance;
    const perpY = dx / distance;
    
    // Offset based on index - smaller offset to keep lines closer to clubs
    const offset = (index - (total - 1) / 2) * 0.005; // 0.005 degrees offset
    
    return {
      lat: perpY * offset,
      lng: perpX * offset
    };
  };

  // Calculate dot size based on number of transactions
  const getDotSize = (transactionCount) => {
    // Base size is 8px, max size is 20px
    const baseSize = 8;
    const maxSize = 20;
    const minSize = 6;
    
    // Use logarithmic scale to prevent extremely large dots
    const logCount = Math.log(transactionCount + 1);
    const maxLogCount = Math.log(100); // Assume 100 is max reasonable count
    
    const size = baseSize + (logCount / maxLogCount) * (maxSize - baseSize);
    return Math.max(minSize, Math.min(maxSize, size));
  };

  // Calculate non-overlapping positions for dots on both sides of the line
  const calculateDotPositions = (fromPos, toPos, facetData, groupingMode) => {
    const facetEntries = Object.entries(facetData).filter(([_, data]) => data.transfers.length > 0);
    if (facetEntries.length === 0) return [];
    
    // Calculate total width needed for all dots
    const dotSizes = facetEntries.map(([_, data]) => getDotSize(data.transfers.length));
    const totalWidth = dotSizes.reduce((sum, size) => sum + size + 4, 0); // 4px spacing between dots
    
    // Zoom-aware geographic offset calculation
    // Higher zoom = smaller geographic offset (dots closer together)
    // Lower zoom = larger geographic offset (dots farther apart)
    const baseOffset = 0.02; // Base geographic offset
    const zoomFactor = Math.pow(1.95, 10 - zoomLevel); // Adjust based on zoom level
    const geoOffsetPerDot = baseOffset * zoomFactor;
    
    const positions = [];
    let currentOffset = -(facetEntries.length - 1) / 2;
    
    facetEntries.forEach(([facetValue, data], index) => {
      const dotSize = dotSizes[index];
      
      // Calculate perpendicular vector
      const dx = toPos[1] - fromPos[1];
      const dy = toPos[0] - fromPos[0];
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 0) {
        const perpX = -dy / distance;
        const perpY = dx / distance;
        
        const midLat = (fromPos[0] + toPos[0]) / 2;
        const midLng = (fromPos[1] + toPos[1]) / 2;
        
        // Apply zoom-aware offset
        const geoOffset = currentOffset * geoOffsetPerDot;
        
        if (groupingMode === 'none') {
          // For club-to-club transfers, show dots on both sides
          // Add dot on one side of the line
          positions.push({
            facetValue,
            data,
            position: [midLat + perpY * geoOffset, midLng + perpX * geoOffset],
            size: dotSize
          });
          
          // Add dot on the other side of the line (mirrored)
          positions.push({
            facetValue,
            data,
            position: [midLat - perpY * geoOffset, midLng - perpX * geoOffset],
            size: dotSize
          });
        } else {
          // For league-to-league transfers, show only 1 dot per facet
          positions.push({
            facetValue,
            data,
            position: [midLat + perpY * geoOffset, midLng + perpX * geoOffset],
            size: dotSize
          });
        }
      }
      
      currentOffset += 1; // Move to next dot position
    });
    
    return positions;
  };

  // Calculate non-overlapping positions for self-loop dots
  const calculateSelfLoopDotPositions = (fromPos, facetData, groupingMode) => {
    const facetEntries = Object.entries(facetData).filter(([_, data]) => data.transfers.length > 0);
    if (facetEntries.length === 0) return [];
    
    // Zoom-aware radius calculation - much smaller base radius
    const baseRadius = 0.023; // Reduced from 0.3 to 0.05
    const zoomFactor = Math.pow(1.95, 10 - zoomLevel); // Use same zoom factor as regular dots
    const radius = baseRadius * zoomFactor;
    
    const positions = [];
    
    facetEntries.forEach(([facetValue, data], index) => {
      const dotSize = getDotSize(data.transfers.length);
      const angle = (index / facetEntries.length) * 2 * Math.PI;
      
      // Add some randomness to prevent perfect overlap - reduced random offset
      const randomOffset = (Math.random() - 0.5) * 0.005 * zoomFactor; // Reduced from 0.02 to 0.005
      const adjustedRadius = radius + randomOffset;
      
      const dotLat = fromPos[0] + adjustedRadius * Math.cos(angle);
      const dotLng = fromPos[1] + adjustedRadius * Math.sin(angle);
      
      positions.push({
        facetValue,
        data,
        position: [dotLat, dotLng],
        size: dotSize
      });
    });
    
    return positions;
  };

  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  // Determine if we should show grouped markers
  const shouldShowGroupedMarkers = groupingMode !== 'none';
  const groupCenters = getGroupCenters;

  return (
    <>
      <ZoomTracker onZoomChange={setZoomLevel} />
      {/* Club/League Markers */}
      {shouldShowGroupedMarkers && groupCenters ? (
        // Show grouped markers
        Object.entries(groupCenters).map(([entity, center]) => (
          <Marker
            key={entity}
            position={[center.lat, center.lng]}
            icon={getClubIcon(entity, center.country)}
          >
            <Popup className="club-popup">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div>
                  <h3>{entity}</h3>
                  <p>{filteredTransfers.filter(t => {
                    if (groupingMode === 'league') return getClubById(t.fromClubId)?.league === entity || getClubById(t.toClubId)?.league === entity;
                    if (groupingMode === 'position') return t.position === entity.split(' (')[0];
                    if (groupingMode === 'date') return new Date(t.date).toISOString().slice(0, 7) === entity.split(' (')[0];
                    return false;
                  }).length} transfers</p>
                </div>
              </div>
            </Popup>
          </Marker>
        ))
      ) : (
        // Show individual club markers
        clusteredClubsData.map(club => (
          <Marker
            key={club.id}
            position={[club.lat, club.lng]}
            icon={getClubIcon(club.league, club.country)}
          >
            <Popup className="club-popup">
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.9)', 
                color: 'white', 
                padding: '16px', 
                borderRadius: '8px',
                minWidth: '300px',
                maxWidth: '400px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <img 
                    src={club.crest} 
                    alt={`${club.name} crest`} 
                    style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                  />
                  <div>
                    <h3 style={{ color: 'white', margin: 0, fontSize: '16px' }}>{club.name}</h3>
                    <p style={{ color: '#ccc', margin: 0, fontSize: '12px' }}>{club.league} • {club.country}</p>
                  </div>
                </div>
                
                {/* Show transfers for this club */}
                {(() => {
                  const outgoingTransfers = filteredTransfers.filter(transfer => 
                    transfer.fromClubId === club.id
                  );
                  const incomingTransfers = filteredTransfers.filter(transfer => 
                    transfer.toClubId === club.id
                  );
                  
                  if (outgoingTransfers.length === 0 && incomingTransfers.length === 0) {
                    return <p style={{ fontSize: '12px', color: '#ccc', fontStyle: 'italic' }}>No transfers in current filter</p>;
                  }
                  
                  return (
                    <div>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: 'white' }}>
                        Transfers ({outgoingTransfers.length + incomingTransfers.length})
                      </h4>
                      
                      {/* Two tabs for In and Out transfers - matching grouped mode style */}
                      <div>
                        <div style={{ display: 'flex', marginBottom: '10px' }}>
                          <button
                            onClick={() => setActiveClubTab(prev => ({ ...prev, [club.id]: 'in' }))}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: activeClubTab[club.id] === 'in' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)',
                              border: '1px solid #4CAF50',
                              color: '#4CAF50',
                              fontWeight: '600',
                              fontSize: '12px',
                              cursor: 'pointer',
                              borderRadius: '4px 0 0 4px'
                            }}
                          >
                            In ({incomingTransfers.length})
                          </button>
                          <button
                            onClick={() => setActiveClubTab(prev => ({ ...prev, [club.id]: 'out' }))}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: activeClubTab[club.id] === 'out' ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255, 107, 53, 0.2)',
                              border: '1px solid #FF6B35',
                              color: '#FF6B35',
                              fontWeight: '600',
                              fontSize: '12px',
                              cursor: 'pointer',
                              borderRadius: '0 4px 4px 0',
                              marginLeft: '-1px'
                            }}
                          >
                            Out ({outgoingTransfers.length})
                          </button>
                        </div>
                        
                        {/* Tab Content - Only show active tab */}
                        {(!activeClubTab[club.id] || activeClubTab[club.id] === 'in') && (
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {incomingTransfers.slice(0, 10).map((transfer, index) => {
                              const fromClub = getClubById(transfer.fromClubId);
                              
                              return (
                                <div key={`in-${index}`} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '6px', 
                                  marginBottom: '6px',
                                  padding: '6px',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '4px'
                                }}>
                                  <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#4CAF50',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {transfer.player.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                  </div>
                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', flex: 1 }}>{transfer.player}</span>
                                  <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                </div>
                              );
                            })}
                            {incomingTransfers.length > 10 && (
                              <p style={{ fontSize: '10px', color: '#ccc', textAlign: 'center', marginTop: '5px' }}>
                                +{incomingTransfers.length - 10} more incoming transfers
                              </p>
                            )}
                          </div>
                        )}
                        
                        {activeClubTab[club.id] === 'out' && (
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {outgoingTransfers.slice(0, 10).map((transfer, index) => {
                              const toClub = getClubById(transfer.toClubId);
                              
                              return (
                                <div key={`out-${index}`} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '6px', 
                                  marginBottom: '6px',
                                  padding: '6px',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '4px'
                                }}>
                                  <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#FF6B35',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {transfer.player.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                  </div>
                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', flex: 1 }}>{transfer.player}</span>
                                  <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                </div>
                              );
                            })}
                            {outgoingTransfers.length > 10 && (
                              <p style={{ fontSize: '10px', color: '#ccc', textAlign: 'center', marginTop: '5px' }}>
                                +{outgoingTransfers.length - 10} more outgoing transfers
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </Popup>
          </Marker>
        ))
      )}

      {/* Transfer Lines */}
      {Object.entries(groupedTransfers).map(([key, group]) => {
        const fromPos = shouldShowGroupedMarkers && groupCenters?.[group.fromEntity] 
          ? [groupCenters[group.fromEntity].lat, groupCenters[group.fromEntity].lng]
          : [group.fromClub.lat, group.fromClub.lng];
        
        const toPos = shouldShowGroupedMarkers && groupCenters?.[group.toEntity]
          ? [groupCenters[group.toEntity].lat, groupCenters[group.toEntity].lng]
          : [group.toClub.lat, group.toClub.lng];

        // Calculate offset for multiple transfers between same entities
        const offset = calculateOffset(fromPos, toPos, 0, 1);

         // Check if this is a self-loop (same league)
         const isSelfLoop = group.fromEntity === group.toEntity;

        return (
           <React.Fragment key={key}>
          <Polyline
               positions={isSelfLoop ? 
                 // Create a curved self-loop
                 [
                   [fromPos[0] + offset.lat, fromPos[1] + offset.lng],
                   [fromPos[0] + offset.lat + 0.5, fromPos[1] + offset.lng + 0.5],
                   [fromPos[0] + offset.lat, fromPos[1] + offset.lng + 1],
                   [fromPos[0] + offset.lat - 0.5, fromPos[1] + offset.lng + 0.5],
                   [fromPos[0] + offset.lat, fromPos[1] + offset.lng]
                 ] :
                 [
              [fromPos[0] + offset.lat, fromPos[1] + offset.lng],
              [toPos[0] + offset.lat, toPos[1] + offset.lng]
                 ]
               }
            color={getLineColor(group.netValue)}
               weight={getLineWidth(group.transfers.length)}
               opacity={1.0}
            className="transfer-line"
          >
               <Popup className="facet-popup">
                 <div style={{ 
                   minWidth: '280px', 
                   maxWidth: '320px',
                   backgroundColor: '#1a1a1a',
                   color: '#ffffff',
                   borderRadius: '8px',
                   padding: '12px',
                   fontFamily: 'Arial, sans-serif'
                 }}>
                   <div style={{ 
                     borderBottom: '2px solid #4CAF50', 
                     marginBottom: '12px',
                     paddingBottom: '8px'
                   }}>
                     <h3 style={{ 
                       margin: '0 0 4px 0', 
                       fontSize: '16px', 
                       color: '#4CAF50',
                       fontWeight: 'bold'
                     }}>All Transfers</h3>
                     <p style={{ 
                       margin: '2px 0', 
                       fontSize: '12px', 
                       color: '#cccccc'
                     }}><strong>{group.bidirectional ? `${group.fromEntity} ↔ ${group.toEntity}` : `${group.fromEntity} → ${group.toEntity}`}</strong></p>
                     <p style={{ 
                       margin: '2px 0', 
                       fontSize: '11px', 
                       color: '#888888'
                     }}><strong>Total Value:</strong> {formatAmount(group.netValue)} • <strong>Count:</strong> {group.transfers.length}</p>
                   </div>
                   
                   {group.bidirectional ? (
                     // Two tabs for bidirectional transfers
              <div>
                       <div style={{ 
                         display: 'flex', 
                         borderBottom: '1px solid #333', 
                         marginBottom: '8px',
                         borderRadius: '4px',
                         overflow: 'hidden'
                       }}>
                         <button 
                           style={{ 
                             flex: 1, 
                             padding: '6px 8px', 
                             border: 'none', 
                             background: '#2d2d2d', 
                             color: '#ffffff',
                             cursor: 'pointer',
                             fontSize: '10px',
                             borderBottom: '2px solid #4CAF50'
                           }}
                           onClick={(e) => {
                             e.target.parentElement.parentElement.querySelector('.tab-content-1').style.display = 'block';
                             e.target.parentElement.parentElement.querySelector('.tab-content-2').style.display = 'none';
                             e.target.style.borderBottom = '2px solid #4CAF50';
                             e.target.nextElementSibling.style.borderBottom = 'none';
                           }}
                         >
                           {group.fromEntity} → {group.toEntity} ({group.transfers.filter(transfer => {
                             const fromClub = getClubById(transfer.fromClubId);
                             const toClub = getClubById(transfer.toClubId);
                             return fromClub?.league === group.fromEntity && toClub?.league === group.toEntity;
                           }).length})
                         </button>
                         <button 
                           style={{ 
                             flex: 1, 
                             padding: '6px 8px', 
                             border: 'none', 
                             background: '#2d2d2d', 
                             color: '#ffffff',
                             cursor: 'pointer',
                             fontSize: '10px',
                             borderBottom: 'none'
                           }}
                           onClick={(e) => {
                             e.target.parentElement.parentElement.querySelector('.tab-content-1').style.display = 'none';
                             e.target.parentElement.parentElement.querySelector('.tab-content-2').style.display = 'block';
                             e.target.style.borderBottom = '2px solid #4CAF50';
                             e.target.previousElementSibling.style.borderBottom = 'none';
                           }}
                         >
                           {group.toEntity} → {group.fromEntity} ({group.transfers.filter(transfer => {
                             const fromClub = getClubById(transfer.fromClubId);
                             const toClub = getClubById(transfer.toClubId);
                             return fromClub?.league === group.toEntity && toClub?.league === group.fromEntity;
                           }).length})
                         </button>
                       </div>
                       
                       {/* Tab 1: A to B transfers */}
                       <div className="tab-content-1" style={{ display: 'block' }}>
                         <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                           {group.transfers
                             .filter(transfer => {
                               const fromClub = getClubById(transfer.fromClubId);
                               const toClub = getClubById(transfer.toClubId);
                               return fromClub?.league === group.fromEntity && toClub?.league === group.toEntity;
                             })
                             .map(transfer => {
                               const fromClub = getClubById(transfer.fromClubId);
                               const toClub = getClubById(transfer.toClubId);
                               return (
                                 <div key={transfer.id} style={{ 
                                   border: '1px solid #333', 
                                   borderRadius: '4px', 
                                   padding: '6px', 
                                   marginBottom: '4px',
                                   backgroundColor: '#2a2a2a'
                                 }}>
                                   <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                     <img 
                                       src={transfer.playerImage || '/images/player-placeholder.svg'} 
                                       alt={transfer.player}
                                       style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                       onError={(e) => {
                                         e.target.src = '/images/player-placeholder.svg';
                                       }}
                                     />
                                     <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                                     <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                   </div>
                                   <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                                     <img 
                                       src={fromClub?.crest || '/images/crest-placeholder.svg'} 
                                       alt={fromClub?.name}
                                       style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                       onError={(e) => {
                                         e.target.src = '/images/crest-placeholder.svg';
                                       }}
                                     />
                                     <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                                     <span style={{ color: '#666666' }}>→</span>
                                     <img 
                                       src={toClub?.crest || '/images/crest-placeholder.svg'} 
                                       alt={toClub?.name}
                                       style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                       onError={(e) => {
                                         e.target.src = '/images/crest-placeholder.svg';
                                       }}
                                     />
                                     <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                                   </div>
                                   <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                                 </div>
                               );
                             })}
                         </div>
                       </div>
                       
                       {/* Tab 2: B to A transfers */}
                       <div className="tab-content-2" style={{ display: 'none' }}>
                         <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                           {group.transfers
                             .filter(transfer => {
                               const fromClub = getClubById(transfer.fromClubId);
                               const toClub = getClubById(transfer.toClubId);
                               return fromClub?.league === group.toEntity && toClub?.league === group.fromEntity;
                             })
                             .map(transfer => {
                               const fromClub = getClubById(transfer.fromClubId);
                               const toClub = getClubById(transfer.toClubId);
                               return (
                                 <div key={transfer.id} style={{ 
                                   border: '1px solid #333', 
                                   borderRadius: '4px', 
                                   padding: '6px', 
                                   marginBottom: '4px',
                                   backgroundColor: '#2a2a2a'
                                 }}>
                                   <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                     <img 
                                       src={transfer.playerImage || '/images/player-placeholder.svg'} 
                                       alt={transfer.player}
                                       style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                       onError={(e) => {
                                         e.target.src = '/images/player-placeholder.svg';
                                       }}
                                     />
                                     <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                                     <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                   </div>
                                   <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                                     <img 
                                       src={fromClub?.crest || '/images/crest-placeholder.svg'} 
                                       alt={fromClub?.name}
                                       style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                       onError={(e) => {
                                         e.target.src = '/images/crest-placeholder.svg';
                                       }}
                                     />
                                     <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                                     <span style={{ color: '#666666' }}>→</span>
                                     <img 
                                       src={toClub?.crest || '/images/crest-placeholder.svg'} 
                                       alt={toClub?.name}
                                       style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                       onError={(e) => {
                                         e.target.src = '/images/crest-placeholder.svg';
                                       }}
                                     />
                                     <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                                   </div>
                                   <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                                 </div>
                               );
                             })}
                         </div>
                       </div>
                     </div>
                   ) : (
                     // Single tab for unidirectional transfers
                     <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                       {group.transfers.map(transfer => {
                         const fromClub = getClubById(transfer.fromClubId);
                         const toClub = getClubById(transfer.toClubId);
                         return (
                           <div key={transfer.id} style={{ 
                             border: '1px solid #333', 
                             borderRadius: '4px', 
                             padding: '6px', 
                             marginBottom: '4px',
                             backgroundColor: '#2a2a2a'
                           }}>
                             <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                               <img 
                                 src={transfer.playerImage || '/images/player-placeholder.svg'} 
                                 alt={transfer.player}
                                 style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                 onError={(e) => {
                                   e.target.src = '/images/player-placeholder.svg';
                                 }}
                               />
                               <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                               <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                             </div>
                             <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                               <img 
                                 src={fromClub?.crest || '/images/crest-placeholder.svg'} 
                                 alt={fromClub?.name}
                                 style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                 onError={(e) => {
                                   e.target.src = '/images/crest-placeholder.svg';
                                 }}
                               />
                               <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                               <span style={{ color: '#666666' }}>→</span>
                               <img 
                                 src={toClub?.crest || '/images/crest-placeholder.svg'} 
                                 alt={toClub?.name}
                                 style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                 onError={(e) => {
                                   e.target.src = '/images/crest-placeholder.svg';
                                 }}
                               />
                               <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                             </div>
                             <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                           </div>
                         );
                       })}
                  </div>
                )}
              </div>
            </Popup>
          </Polyline>

                           {/* Facet Dots for Grouping */}
              {groupingMode !== 'none' && group.facetBreakdown && !isSelfLoop && (
                calculateDotPositions(fromPos, toPos, group.facetBreakdown, groupingMode).map(({ facetValue, data: facetData, position, size: dotSize }) => {
                  // Format facet value for display
                  let displayValue = facetValue;
                  if (groupingMode === 'date') {
                    const [year, month] = facetValue.split('-');
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    displayValue = `${monthNames[parseInt(month) - 1]} ${year}`;
                  }
                  
                  const iconSize = [dotSize, dotSize];
                  const iconAnchor = [dotSize / 2, dotSize / 2];
                  
                  return (
                    <Marker
                      key={`${key}-${facetValue}`}
                      position={position}
                      icon={L.divIcon({
                        className: 'facet-dot',
                        html: `<div style="
                          width: ${dotSize}px; 
                          height: ${dotSize}px; 
                          background-color: ${getFacetColor(facetValue, groupingMode)}; 
                          border-radius: 50%; 
                          cursor: pointer;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          color: white;
                          font-size: ${Math.max(8, dotSize * 0.4)}px;
                          font-weight: bold;
                          text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
                          line-height: 1;
                        ">${facetData.transfers.length}</div>`,
                        iconSize: iconSize,
                        iconAnchor: iconAnchor
                      })}
                      title={`${displayValue} (${facetData.transfers.length} transfers)`}
                    >
                                                                                           <Popup className="facet-popup">
                          <div style={{ 
                            minWidth: '280px', 
                            maxWidth: '320px',
                            backgroundColor: '#1a1a1a',
                            color: '#ffffff',
                            borderRadius: '8px',
                            padding: '12px',
                            fontFamily: 'Arial, sans-serif'
                          }}>
                            <div style={{ 
                              borderBottom: '2px solid #4CAF50', 
                              marginBottom: '12px',
                              paddingBottom: '8px'
                            }}>
                              <h3 style={{ 
                                margin: '0 0 4px 0', 
                                fontSize: '16px', 
                                color: '#4CAF50',
                                fontWeight: 'bold'
                              }}>{displayValue} Transfers</h3>
                              <p style={{ 
                                margin: '2px 0', 
                                fontSize: '12px', 
                                color: '#cccccc'
                              }}><strong>{group.bidirectional ? `${group.fromEntity} ↔ ${group.toEntity}` : `${group.fromEntity} → ${group.toEntity}`}</strong></p>
                              <p style={{ 
                                margin: '2px 0', 
                                fontSize: '11px', 
                                color: '#888888'
                              }}><strong>Total Value:</strong> {formatAmount(facetData.value)} • <strong>Count:</strong> {facetData.transfers.length}</p>
                            </div>
                            
                            {group.bidirectional ? (
                              // Two tabs for bidirectional transfers
                              <div>
                                <div style={{ 
                                  display: 'flex', 
                                  borderBottom: '1px solid #333', 
                                  marginBottom: '8px',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <button 
                                    style={{ 
                                      flex: 1, 
                                      padding: '6px 8px', 
                                      border: 'none', 
                                      background: '#2d2d2d', 
                                      color: '#ffffff',
                                      cursor: 'pointer',
                                      fontSize: '10px',
                                      borderBottom: '2px solid #4CAF50'
                                    }}
                                    onClick={(e) => {
                                      e.target.parentElement.parentElement.querySelector('.tab-content-1').style.display = 'block';
                                      e.target.parentElement.parentElement.querySelector('.tab-content-2').style.display = 'none';
                                      e.target.style.borderBottom = '2px solid #4CAF50';
                                      e.target.nextElementSibling.style.borderBottom = 'none';
                                    }}
                                  >
                                    {group.fromEntity} → {group.toEntity} ({facetData.transfers.filter(transfer => {
                                      const fromClub = getClubById(transfer.fromClubId);
                                      const toClub = getClubById(transfer.toClubId);
                                      return fromClub?.league === group.fromEntity && toClub?.league === group.toEntity;
                                    }).length})
                                  </button>
                                  <button 
                                    style={{ 
                                      flex: 1, 
                                      padding: '6px 8px', 
                                      border: 'none', 
                                      background: '#2d2d2d', 
                                      color: '#ffffff',
                                      cursor: 'pointer',
                                      fontSize: '10px',
                                      borderBottom: 'none'
                                    }}
                                    onClick={(e) => {
                                      e.target.parentElement.parentElement.querySelector('.tab-content-1').style.display = 'none';
                                      e.target.parentElement.parentElement.querySelector('.tab-content-2').style.display = 'block';
                                      e.target.style.borderBottom = '2px solid #4CAF50';
                                      e.target.previousElementSibling.style.borderBottom = 'none';
                                    }}
                                  >
                                    {group.toEntity} → {group.fromEntity} ({facetData.transfers.filter(transfer => {
                                      const fromClub = getClubById(transfer.fromClubId);
                                      const toClub = getClubById(transfer.toClubId);
                                      return fromClub?.league === group.toEntity && toClub?.league === group.fromEntity;
                                    }).length})
                                  </button>
                                </div>
                                
                                {/* Tab 1: A to B transfers */}
                                <div className="tab-content-1" style={{ display: 'block' }}>
                                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {facetData.transfers
                                      .filter(transfer => {
                                        const fromClub = getClubById(transfer.fromClubId);
                                        const toClub = getClubById(transfer.toClubId);
                                        return fromClub?.league === group.fromEntity && toClub?.league === group.toEntity;
                                      })
                                      .map(transfer => {
                                        const fromClub = getClubById(transfer.fromClubId);
                                        const toClub = getClubById(transfer.toClubId);
                                        return (
                                          <div key={transfer.id} style={{ 
                                            border: '1px solid #333', 
                                            borderRadius: '4px', 
                                            padding: '6px', 
                                            marginBottom: '4px',
                                            backgroundColor: '#2a2a2a'
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                              <img 
                                                src={transfer.playerImage || '../public/images/player-placeholder.svg'} 
                                                alt={transfer.player}
                                                style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                  e.target.src = '../public/images/player-placeholder.svg';
                                                }}
                                              />
                                              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                                              <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                                              <img 
                                                src={fromClub?.crest || '../public/images/crest-placeholder.svg'} 
                                                alt={fromClub?.name}
                                                style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                  e.target.src = '../public/images/placeholder.svg';
                                                }}
                                              />
                                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                                              <span style={{ color: '#666666' }}>→</span>
                                              <img 
                                                src={toClub?.crest || '../public/images/crest-placeholder.svg'} 
                                                alt={toClub?.name}
                                                style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                  e.target.src = '../public/images/placeholder.svg';
                                                }}
                                              />
                                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                                            </div>
                                            <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                                
                                {/* Tab 2: B to A transfers */}
                                <div className="tab-content-2" style={{ display: 'none' }}>
                                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {facetData.transfers
                                      .filter(transfer => {
                                        const fromClub = getClubById(transfer.fromClubId);
                                        const toClub = getClubById(transfer.toClubId);
                                        return fromClub?.league === group.toEntity && toClub?.league === group.fromEntity;
                                      })
                                      .map(transfer => {
                                        const fromClub = getClubById(transfer.fromClubId);
                                        const toClub = getClubById(transfer.toClubId);
                                        return (
                                          <div key={transfer.id} style={{ 
                                            border: '1px solid #333', 
                                            borderRadius: '4px', 
                                            padding: '6px', 
                                            marginBottom: '4px',
                                            backgroundColor: '#2a2a2a'
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                              <img 
                                                src={transfer.playerImage || '../public/images/player-placeholder.svg'} 
                                                alt={transfer.player}
                                                style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                  e.target.src = '../public/images/player-placeholder.svg';
                                                }}
                                              />
                                              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                                              <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                                              <img 
                                                src={fromClub?.crest || '../public/images/crest-placeholder.svg'} 
                                                alt={fromClub?.name}
                                                style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                  e.target.src = '../public/images/placeholder.svg';
                                                }}
                                              />
                                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                                              <span style={{ color: '#666666' }}>→</span>
                                              <img 
                                                src={toClub?.crest || '../public/images/crest-placeholder.svg'} 
                                                alt={toClub?.name}
                                                style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                  e.target.src = '../public/images/placeholder.svg';
                                                }}
                                              />
                                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                                            </div>
                                            <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // Single tab for unidirectional transfers
                              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {facetData.transfers.map(transfer => {
                                  const fromClub = getClubById(transfer.fromClubId);
                                  const toClub = getClubById(transfer.toClubId);
                                  return (
                                    <div key={transfer.id} style={{ 
                                      border: '1px solid #333', 
                                      borderRadius: '4px', 
                                      padding: '6px', 
                                      marginBottom: '4px',
                                      backgroundColor: '#2a2a2a'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                        <img 
                                          src={transfer.playerImage || '../public/images/player-placeholder.svg'} 
                                          alt={transfer.player}
                                          style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                          onError={(e) => {
                                            e.target.src = '../public/images/player-placeholder.svg';
                                          }}
                                        />
                                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                                        <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                                        <img 
                                          src={fromClub?.crest || '../public/images/crest-placeholder.svg'} 
                                          alt={fromClub?.name}
                                          style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                          onError={(e) => {
                                            e.target.src = '../public/images/placeholder.svg';
                                          }}
                                        />
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                                        <span style={{ color: '#666666' }}>→</span>
                                        <img 
                                          src={toClub?.crest || '../public/images/crest-placeholder.svg'} 
                                          alt={toClub?.name}
                                          style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                          onError={(e) => {
                                            e.target.src = '../public/images/placeholder.svg';
                                          }}
                                        />
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </Popup>
                    </Marker>
                  );
                })
              )}

                            {/* Self-loop facet dots */}
              {groupingMode !== 'none' && group.facetBreakdown && isSelfLoop && (
                calculateSelfLoopDotPositions(fromPos, group.facetBreakdown, groupingMode).map(({ facetValue, data: facetData, position, size: dotSize }) => {
                  // Format facet value for display
                  let displayValue = facetValue;
                  if (groupingMode === 'date') {
                    const [year, month] = facetValue.split('-');
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    displayValue = `${monthNames[parseInt(month) - 1]} ${year}`;
                  }
                   
                  const iconSize = [dotSize, dotSize];
                  const iconAnchor = [dotSize / 2, dotSize / 2];
                   
                  return (
                    <Marker
                      key={`${key}-${facetValue}`}
                      position={position}
                      icon={L.divIcon({
                        className: 'facet-dot',
                        html: `<div style="
                          width: ${dotSize}px; 
                          height: ${dotSize}px; 
                          background-color: ${getFacetColor(facetValue, groupingMode)}; 
                          border-radius: 50%; 
                          cursor: pointer;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          color: white;
                          font-size: ${Math.max(8, dotSize * 0.4)}px;
                          font-weight: bold;
                          text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
                          line-height: 1;
                        ">${facetData.transfers.length}</div>`,
                        iconSize: iconSize,
                        iconAnchor: iconAnchor
                      })}
                      title={`${displayValue} (${facetData.transfers.length} transfers)`}
                    >
                                             <Popup className="facet-popup">
                                                  <div style={{ 
                            minWidth: '280px', 
                            maxWidth: '320px',
                            backgroundColor: '#1a1a1a',
                            color: '#ffffff',
                            borderRadius: '8px',
                            padding: '12px',
                            fontFamily: 'Arial, sans-serif'
                          }}>
                            <div style={{ 
                              borderBottom: '2px solid #4CAF50', 
                              marginBottom: '12px',
                              paddingBottom: '8px'
                            }}>
                              <h3 style={{ 
                                margin: '0 0 4px 0', 
                                fontSize: '16px', 
                                color: '#4CAF50',
                                fontWeight: 'bold'
                              }}>{displayValue} Transfers</h3>
                              <p style={{ 
                                margin: '2px 0', 
                                fontSize: '12px', 
                                color: '#cccccc'
                              }}><strong>{group.bidirectional ? `${group.fromEntity} ↔ ${group.toEntity}` : `${group.fromEntity} → ${group.toEntity}`}</strong></p>
                              <p style={{ 
                                margin: '2px 0', 
                                fontSize: '11px', 
                                color: '#888888'
                              }}><strong>Total Value:</strong> {formatAmount(facetData.value)} • <strong>Count:</strong> {facetData.transfers.length}</p>
                            </div>
                            
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                              {facetData.transfers.map(transfer => {
                                const fromClub = getClubById(transfer.fromClubId);
                                const toClub = getClubById(transfer.toClubId);
                                return (
                                  <div key={transfer.id} style={{ 
                                    border: '1px solid #333', 
                                    borderRadius: '4px', 
                                    padding: '6px', 
                                    marginBottom: '4px',
                                    backgroundColor: '#2a2a2a'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                      <img 
                                        src={transfer.playerImage || '../public/images/player-placeholder.svg'} 
                                        alt={transfer.player}
                                        style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.src = '../public/images/player-placeholder.svg';
                                        }}
                                      />
                                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>{transfer.player}</span>
                                      <span style={{ fontSize: '10px', color: '#4CAF50', marginLeft: 'auto' }}>{formatAmount(transfer.amount)}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: '#888888' }}>
                                      <img 
                                        src={fromClub?.crest || '../public/images/crest-placeholder.svg'} 
                                        alt={fromClub?.name}
                                        style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                        onError={(e) => {
                                          e.target.src = '../public/images/placeholder.svg';
                                        }}
                                      />
                                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromClub?.name}</span>
                                      <span style={{ color: '#666666' }}>→</span>
                                      <img 
                                        src={toClub?.crest || '../public/images/crest-placeholder.svg'} 
                                        alt={toClub?.name}
                                        style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                                        onError={(e) => {
                                          e.target.src = '../public/images/placeholder.svg';
                                        }}
                                      />
                                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toClub?.name}</span>
                                    </div>
                                    <div style={{ fontSize: '8px', color: '#666666', marginTop: '2px' }}>{transfer.date}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                       </Popup>
                    </Marker>
                  );
                })
              )}
           </React.Fragment>
        );
      })}
      
      {/* Show More Button */}
      {totalFilteredTransfers > currentBatch * 100 && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px 16px',
          borderRadius: '20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
          <button
            onClick={() => setCurrentBatch(prev => prev + 1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#333',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            Show Next 100 ({totalFilteredTransfers - (currentBatch * 100)} remaining)
          </button>
        </div>
      )}
    </>
  );
};

export default TransferMap;
